<script setup lang="ts">
import type {
  NotificationKind,
  NotificationRow,
  NotificationSeverity,
} from '@/types/notifications';
import { onNotificationToast } from '@/utils/notifications-toast-bus';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const toast = useToast();
const { t } = useI18n();

/** Max number of toasts visible at once. */
const MAX_VISIBLE = 3;
/** Queue of pending notifications when capacity is full. */
const pendingQueue: NotificationRow[] = [];
/** Number of currently visible toasts. */
let visibleCount = 0;
/** Increasing id for each shown toast to track lifecycle. */
let nextToastId = 1;
/** Map toast id to its auto-close timeout to cancel on manual close. */
const toastTimeouts = new Map<number, number>();
/** Track already released ids to avoid double accounting. */
const releasedIds = new Set<number>();

/** Minimal shape for PrimeVue Toast container slot message. */
interface ToastContainerMessage {
  detail?: unknown;
  summary?: string;
}

/**
 * Map notification kind and severity to Tailwind background and border color classes.
 */
function colorClasses(kind: NotificationKind, severity: NotificationSeverity): string {
  // Alarm kind
  if (kind === 'alarm') {
    switch (severity) {
      case 'critical':
      case 'error':
        return 'bg-red-500 border-red-500 text-black';
      case 'warning':
        return 'bg-amber-500 border-amber-500 text-black';
      default:
        return 'bg-white dark:bg-surface-900 border-surface-200 dark:border-surface-800 text-surface-900 dark:text-surface-0';
    }
  }
  // Notification kind
  if (kind === 'notification') {
    switch (severity) {
      case 'critical':
      case 'error':
        return 'bg-red-500 border-red-500 text-black';
      case 'warning':
        return 'bg-amber-500 border-amber-500 text-black';
      default:
        return 'bg-white dark:bg-surface-900 border-surface-800 dark:border-surface-200 text-surface-900 dark:text-surface-0';
    }
  }
  return 'Unrecognized notification';
}

/**
 * Map notification kind and severity to a short heading.
 */
function heading(kind: NotificationKind, severity: NotificationSeverity): string {
  // Alarm kind
  if (kind === 'alarm') {
    switch (severity) {
      case 'critical':
        return t('notifications.heading.alarm_critical');
      case 'error':
        return t('notifications.heading.alarm_error');
      case 'warning':
        return t('notifications.heading.alarm_warning');
      case 'info':
        return t('notifications.heading.alarm_info');
      default:
        return t('notifications.heading.alarm_default');
    }
  }
  // Notification kind
  if (kind === 'notification') {
    switch (severity) {
      case 'critical':
        return t('notifications.heading.notification_critical');
      case 'error':
        return t('notifications.heading.notification_error');
      case 'warning':
        return t('notifications.heading.notification_warning');
      case 'info':
        return t('notifications.heading.notification_info');
      default:
        return t('notifications.heading.notification_default');
    }
  }
  return t('notifications.heading.unrecognized');
}

/**
 * Resolve device name from notification data with i18n fallback.
 */
function resolveDeviceName(data: Record<string, unknown> | undefined | null): string {
  const name = String((data as unknown as { device_name?: string })?.device_name ?? '');
  return name || t('common.not_available');
}

/**
 * Translate notification preset key using i18n with device_name interpolation.
 * Falls back to provided defaultText if translation key is missing.
 */
function translatePreset(
  key: string | null | undefined,
  isTitle: boolean,
  defaultText: string,
  row: NotificationRow,
): string {
  if (!key || typeof key !== 'string') return defaultText;
  const fullKey = `notifications.presets.${isTitle ? 'titles' : 'bodies'}.${key}`;
  const translated = t(fullKey, { device_name: resolveDeviceName(row.data) });
  // vue-i18n returns the key itself when missing; detect and fallback
  return translated === fullKey ? defaultText : (translated as string);
}

/** Mobile detection based on viewport width. */
const isMobile = ref(false);
function updateIsMobile() {
  isMobile.value = window.matchMedia('(max-width: 480px)').matches;
}

onMounted(() => {
  updateIsMobile();
  window.addEventListener('resize', updateIsMobile);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIsMobile);
  // Cleanup local state to avoid leaks
  for (const t of toastTimeouts.values()) {
    window.clearTimeout(t);
  }
  toastTimeouts.clear();
  releasedIds.clear();
  pendingQueue.length = 0;
  visibleCount = 0;
});

/**
 * Truncate a string to a max length with ellipsis.
 */
function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1))}…`;
}

/**
 * Show a toast for a notification row using a custom, clickable template.
 */
function showToast(row: NotificationRow) {
  const title = heading(row.kind, row.severity);
  const showTitle = !isMobile.value; // hide notification.title on mobile
  const subtitle = showTitle ? translatePreset(row.title, true, row.title, row) : '';
  const body = translatePreset(row.body ?? undefined, false, row.body ?? '', row);
  const compactBody = isMobile.value ? truncate(body, 80) : truncate(body, 160);

  const id = nextToastId++;
  visibleCount++;

  const lifeMs = 6000;
  // Schedule release when the toast auto-closes
  const timeout = window.setTimeout(() => {
    releaseToast(id);
  }, lifeMs + 50);
  toastTimeouts.set(id, timeout);

  toast.add({
    group: 'notifications',
    life: lifeMs,
    // use container slot via pt to keep Tailwind colors
    summary: title,
    detail: JSON.stringify({
      id,
      subtitle,
      body: compactBody,
      classes: colorClasses(row.kind, row.severity),
    }),
  });
}

/** Release a toast slot and show next pending if any. */
function releaseToast(id: number) {
  if (releasedIds.has(id)) return;
  releasedIds.add(id);
  const t = toastTimeouts.get(id);
  if (typeof t === 'number') {
    window.clearTimeout(t);
    toastTimeouts.delete(id);
  }
  if (visibleCount > 0) visibleCount--;
  // Drain queue if capacity available
  while (visibleCount < MAX_VISIBLE && pendingQueue.length > 0) {
    const next = pendingQueue.shift();
    if (next) {
      showToast(next);
    }
  }
}

/** Handle click on toast container: close UI and release slot immediately. */
function handleToastClick(message: ToastContainerMessage, closeCallback: () => void) {
  try {
    const { id } = parseDetail(message?.detail);
    if (typeof id === 'number') {
      releaseToast(id);
    }
  } catch {}
  closeCallback();
}

let off: (() => void) | null = null;
onMounted(() => {
  off = onNotificationToast(({ row }) => {
    if (visibleCount < MAX_VISIBLE) {
      showToast(row);
    } else {
      pendingQueue.push(row);
    }
  });
});
onBeforeUnmount(() => {
  off?.();
});

/**
 * Safely parse detail payload coming from the toast service. If parsing fails,
 * fallback to treating detail as plain text body.
 */
function parseDetail(detail: unknown): {
  id?: number;
  subtitle: string;
  body: string;
  classes: string;
} {
  try {
    if (typeof detail === 'string') {
      const parsed = JSON.parse(detail);
      if (parsed && typeof parsed === 'object') {
        const { id, subtitle = '', body = '', classes = '' } = parsed as Record<string, unknown>;
        return {
          id: typeof id === 'number' ? id : undefined,
          subtitle: typeof subtitle === 'string' ? subtitle : '',
          body: typeof body === 'string' ? body : '',
          classes: typeof classes === 'string' ? classes : '',
        };
      }
      return { id: undefined, subtitle: '', body: detail, classes: '' } as unknown as {
        id?: number;
        subtitle: string;
        body: string;
        classes: string;
      };
    }
    return { id: undefined, subtitle: '', body: '', classes: '' };
  } catch {
    return {
      id: undefined,
      subtitle: '',
      body: typeof detail === 'string' ? detail : '',
      classes: '',
    };
  }
}
</script>

<template>
  <!-- Positioned bottom-center; use headless container to fully control UI -->
  <Toast position="bottom-center" group="notifications" :pt="{ root: { class: 'pointer-events-none z-[1400]' } }">
    <template #container="{ message, closeCallback }">
      <button
        type="button"
        class="relative z-[1500] pointer-events-auto block w-[min(92vw,42rem)] mx-auto text-left rounded-md border shadow-lg focus:outline-none focus:ring-2 focus:ring-white/40 transition"
        :class="parseDetail(message.detail).classes"
        @click="handleToastClick(message, closeCallback)"
      >
        <div class="p-3 md:p-4">
          <div class="flex items-start gap-3">
            <i class="pi pi-bell mt-0.5"></i>
            <div class="flex-1 min-w-0">
              <div class="font-semibold text-base leading-tight">{{ message.summary }}</div>
              <div v-if="parseDetail(message.detail).subtitle" class="text-sm opacity-90 truncate">
                {{ parseDetail(message.detail).subtitle }}
              </div>
              <div class="text-sm opacity-95 whitespace-pre-line mt-1 line-clamp-3">
                {{ parseDetail(message.detail).body }}
              </div>
            </div>
          </div>
        </div>
      </button>
    </template>
  </Toast>

  <!-- Toast for copy notifications at bottom-center -->
  <Toast position="bottom-center" group="bc" />
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>


